import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface FerrerEspecialidad {
  especialidad: string
  total: number
}

interface FerrerMedicosPeruData {
  titulo: string
  subtitulo: string
  fecha_generacion: string
  total_general: number
  total_especialidades: number
  especialidades: FerrerEspecialidad[]
  top_especialidades?: FerrerEspecialidad[]
}

const COLORES_ESPECIALIDADES = [
  'rgba(59, 130, 246, 0.7)',   // Blue
  'rgba(16, 185, 129, 0.7)',   // Green
  'rgba(139, 92, 246, 0.7)',   // Purple
  'rgba(245, 158, 11, 0.7)',   // Amber
  'rgba(239, 68, 68, 0.7)',    // Red
  'rgba(236, 72, 153, 0.7)',   // Pink
  'rgba(14, 165, 233, 0.7)',   // Sky
]

const COLORES_BORDE = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#ef4444', '#ec4899', '#0ea5e9',
]

export function FerrerMedicosPeruPage() {
  const { data, isLoading, error } =
    useDashReport<FerrerMedicosPeruData>('ferrer/medicos-peru-0126')

  const [filtroActual, setFiltroActual] = useState('todas')
  const [seccionExpandida, setSeccionExpandida] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const chevronRef = useRef<SVGSVGElement>(null)

  const especialidadesFiltradas = useMemo(() => {
    if (!data) return []
    if (filtroActual === 'todas') return data.especialidades
    return data.especialidades.filter(e => e.especialidad === filtroActual)
  }, [data, filtroActual])

  const totalFiltrado = useMemo(() => {
    return especialidadesFiltradas.reduce((sum, e) => sum + e.total, 0)
  }, [especialidadesFiltradas])

  const tituloResumen = useMemo(() => {
    if (filtroActual === 'todas') return 'Resumen General'
    return filtroActual
  }, [filtroActual])

  const labelTotal = useMemo(() => {
    if (filtroActual === 'todas') return 'Total Médicos'
    return 'Total ' + filtroActual
  }, [filtroActual])

  const totalEspecialidades = useMemo(() => {
    if (filtroActual === 'todas') return data?.total_especialidades ?? 0
    return 1
  }, [filtroActual, data])

  const toggleSeccion = useCallback(() => {
    setSeccionExpandida(prev => !prev)
  }, [])

  useEffect(() => {
    const content = contentRef.current
    const chevron = chevronRef.current
    if (!content || !chevron) return

    if (seccionExpandida) {
      content.style.maxHeight = content.scrollHeight + 'px'
      content.style.opacity = '1'
      chevron.style.transform = 'rotate(180deg)'
      chevron.classList.add('expanded')
    } else {
      content.style.maxHeight = '0px'
      content.style.opacity = '0'
      chevron.style.transform = 'rotate(0deg)'
      chevron.classList.remove('expanded')
    }
  }, [seccionExpandida, especialidadesFiltradas])

  const chartEspecialidadData = useMemo(() => ({
    labels: especialidadesFiltradas.map(e => e.especialidad),
    datasets: [{
      label: 'Total Médicos',
      data: especialidadesFiltradas.map(e => e.total),
      backgroundColor: COLORES_ESPECIALIDADES.slice(0, especialidadesFiltradas.length),
      borderColor: COLORES_BORDE.slice(0, especialidadesFiltradas.length),
      borderWidth: 1,
    }],
  }), [especialidadesFiltradas])

  const chartEspecialidadOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 80 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 as const },
        formatter: (value: number) => value.toLocaleString('es-PE'),
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: (value: string | number) =>
            Number(value).toLocaleString('es-PE'),
        },
      },
    },
  }), [])

  const porcentajes = useMemo(() => {
    const total = especialidadesFiltradas.reduce((sum, e) => sum + e.total, 0)
    return especialidadesFiltradas.map(e =>
      parseFloat(((e.total / total) * 100).toFixed(1))
    )
  }, [especialidadesFiltradas])

  const chartPorcentajeData = useMemo(() => ({
    labels: especialidadesFiltradas.map(e => e.especialidad),
    datasets: [{
      label: 'Porcentaje',
      data: porcentajes,
      backgroundColor: COLORES_ESPECIALIDADES.slice(0, especialidadesFiltradas.length),
      borderColor: COLORES_BORDE.slice(0, especialidadesFiltradas.length),
      borderWidth: 1,
    }],
  }), [especialidadesFiltradas, porcentajes])

  const chartPorcentajeOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 2,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 as const },
        formatter: (value: number) => value.toFixed(1) + '%',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grace: '10%',
        ticks: {
          callback: (value: string | number) => value + '%',
        },
      },
    },
  }), [])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
      `}</style>
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img
                  src="/logo-tonal-negro.png"
                  alt="Tonal"
                  className="h-8 lg:h-10 w-auto mr-4 lg:mr-6"
                />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                  {data.fecha_generacion && (
                    <p className="text-xs text-gray-500 mt-1">Generado: {data.fecha_generacion}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="https://enterprise.tonalstrategies.com/pricing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  Calculador
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtro de Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por Especialidad:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFiltroActual('todas')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroActual === 'todas'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>
                {data.especialidades.map((esp) => (
                  <button
                    key={esp.especialidad}
                    onClick={() => setFiltroActual(esp.especialidad)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filtroActual === esp.especialidad
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {esp.especialidad} <span className="text-xs opacity-75">({Math.round(esp.total)})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Estadisticas Generales */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>{tituloResumen}</span>
            </h2>

            {/* Metricas Principales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">
                  {totalFiltrado.toLocaleString('es-PE')}
                </p>
                <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl lg:text-3xl font-bold text-blue-600">
                  {totalEspecialidades}
                </p>
                <p className="text-sm text-gray-600 mt-1">Especialidades</p>
              </div>
            </div>
          </div>

          {/* Graficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Grafica por Especialidad */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Distribución por Especialidad
              </h3>
              <div className="h-64 lg:h-96">
                <Bar
                  key={'esp-' + filtroActual}
                  data={chartEspecialidadData}
                  options={chartEspecialidadOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>

            {/* Grafica de Porcentaje */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                Porcentaje del Total
              </h3>
              <div className="h-64 lg:h-96">
                <Bar
                  key={'pct-' + filtroActual}
                  data={chartPorcentajeData}
                  options={chartPorcentajeOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          </div>

          {/* Tabla de Detalle por Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <button
              onClick={toggleSeccion}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Especialidad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">
                    ({totalEspecialidades} especialidades)
                  </span>
                </div>
                <svg
                  ref={chevronRef}
                  className="w-5 h-5 text-gray-400 transform transition-transform duration-300 animate-pulse-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Médicos</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% del Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {especialidadesFiltradas.map(esp => {
                      const porcentaje = totalFiltrado > 0
                        ? ((esp.total / data.total_general) * 100)
                        : 0
                      return (
                        <tr key={esp.especialidad} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{esp.especialidad}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                            {Math.round(esp.total)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600">
                            {porcentaje.toFixed(1)}%
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2026 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
